import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import {
  GridRowModes,
  DataGrid,
  useGridApiRef,
  useGridSelector,
  gridPageCountSelector,
  gridClasses,
  gridFilteredSortedRowEntriesSelector,
} from "@mui/x-data-grid";
import { checkPermissions } from "../../AccessControl";
import { DataGridContext } from "../../../context/DataGridContext";
import { API_ENDPOINTS, PAGE_TABLE_MAPPING } from "../../../API/apiConfig";
import {
  CustomNoRowsOverlay,
  CustomOverlay,
  computeMutation,
  getNestedValue,
  modiferDataColumns,
  processRowUpdateUtil,
  getTogglableColumns,
  handleRowEditStop,
  onDiscard,
  renderConfirmDialog,
  actionOptions,
} from "../utils";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  darken,
  debounce,
} from "@mui/material";
import CustomColumnMenu from "./components/CustomColumnMenu";
import MuiPagination from "@mui/material/Pagination";
import GridModal from "./components/Modal";
import CustomToolbar from "./components/Toolbar";
import ConfirmBox from "../data grid files/ConfirmBox";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import HistoryModal from "./components/HistoryModal";
import { useAppContext } from "../../../appContext";
import { useUserState } from "../../../context/UserContext";
import { useRowsState, useRowStateUpdater } from "../../../context/RowsContext";
import { getAllRows, getRows } from "../DataGridUtils";
import { toggleSnackbarOpen } from "../../../redux/actions/snackbarActions";
import { useDispatch } from "react-redux";
import { BulkPricingModal } from "./components/BulkPricingModal";
import { CircularProgressWithLabel } from "./components/CircularProgressWithLabel";
import ErrorDialog from "../../grid/components/ErrorDialog";
import { startTransition } from "react";
import { Footer } from "./components/Footer";
import { MultiDeleteDialog } from "./components/MultiDeleteDialog";

export default function DataGridRefactor({
  modalVisible,
  type,
  contextName,
  gridList,
  table,
  setModalConfig,
  modalConfig,
  setModalVisible,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [rowModesModel, setRowModesModel] = React.useState({});
  const ref = useGridApiRef({});
  const historyApiRef = useGridApiRef({});
  const dispatch = useDispatch();
  const getVisibleColumns = () => {
    const visCol = {};
    for (let i = 0; i < gridList.columns.length; i++) {
      const field = gridList.columns[i].field;
      const visible = gridList.columns[i].visible;
      Object.assign(visCol, { [field]: visible });
    }
    return visCol;
  };
  const columns = getVisibleColumns();
  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState(columns);
  const rows = useRowsState();
  const setRows = useRowStateUpdater();
  const [searchValue, setSearchValue] = React.useState("");
  const [open, setOpen] = React.useState({
    status: false,
    id: null,
    msg: null,
    lists: [],
  });
  const { state } = useAppContext();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [historyModalOpen, setHistoryModalOpen] = React.useState({
    open: false,
    row: "",
  });
  const [isSaving, setIsSaving] = React.useState(false);
  const {
    data,
    setData,
    variant,
    setVariant,
    siteFilterValue,
    priceBandFilterValue,
    priceBandColors,
    showDeleted,
    setShowDeleted,
  } = React.useContext(DataGridContext);
  const userPermission = useUserState();
  const [rowLoading, setRowLoading] = React.useState(true);
  const [hasUnsavedRows, setHasUnsavedRows] = React.useState(false);
  const unsavedChangesRef = React.useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });
  const [saveProgress, setSaveProgress] = React.useState({
    openDialog: false,
    progress: 0,
  });

  const navigate = useNavigate();

  const useStyles = makeStyles(() => ({
    headerWrap: {
      whiteSpace: "break-spaces",
      wordWrap: "break-word",
      width: "900px",
    },
  }));
  const classes = useStyles();
  const updateSearchValue = React.useMemo(() => {
    return debounce((newValue) => {
      ref.current.setQuickFilterValues(
        newValue.split(" ").filter((word) => word !== "")
      );
    }, 50);
  }, [ref]);

  const customFilterModel = useLocation();

  function handleSearchValueChange(event) {
    const newValue = event.target.value;
    setSearchValue(newValue);
    updateSearchValue(newValue);
  }

  function handleClearSearchValueChange(event) {
    setSearchValue("");
    updateSearchValue("");
  }

  const [filterModel, setFilterModel] = React.useState(
    customFilterModel?.state?.customFilterModel
      ? customFilterModel?.state?.customFilterModel
      : {
          items: [],
        }
  );

  React.useEffect(() => {
    if (
      showDeleted === false ||
      checkPermissions(userPermission, {
        page: gridList.gridPage,
        action: "undelete",
        strict: true,
      }) === false
    ) {
      setRows(getRows(gridList, data, contextName, table, type));
    } else {
      setRows(getAllRows(gridList, data, contextName, table, type));
    }
    setTimeout(() => {
      setRowLoading(false);
    }, 1000);
  }, [gridList, contextName, data, showDeleted, table, type]);

  let shouldBlock = React.useCallback(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedRows && currentLocation.pathname !== nextLocation.pathname,
    [hasUnsavedRows]
  );
  let blocker = useBlocker(shouldBlock);

  const [errorMessage, setErrorMessage] = React.useState({
    open: false,
    text: null,
    button1text: "",
    button1func: null,
    button2text: "",
    button2func: null,
  });

  const renderProgressDialog = () => {
    const handleEntered = () => {};
    return (
      <Dialog
        maxWidth="xs"
        TransitionProps={{ onEntered: handleEntered }}
        open={saveProgress.openDialog}
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <DialogTitle>Saving Prices</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgressWithLabel value={saveProgress.progress} />

          {Object.keys(unsavedChangesRef.current.unsavedRows).length > 1 &&
            Math.round(saveProgress.progress) >= 100 &&
            !isSaving && (
              <>
                <Typography
                  sx={{ textAlign: "center", mb: 2, mt: 2, userSelect: "none" }}
                >
                  An error occured when processing some rows, some rows were not
                  updated.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                  <Button variant="contained" onClick={saveBulkChanges}>
                    Retry
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSaveProgress({ openDialog: false, progress: 0 });
                      unsavedChangesRef.current = {
                        unsavedRows: {},
                        rowsBeforeChange: {},
                      };
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </>
            )}
          {Object.keys(unsavedChangesRef.current.unsavedRows).length === 0 &&
            Math.round(saveProgress.progress) >= 100 && (
              <Typography
                sx={{ textAlign: "center", mb: 2, mt: 2, userSelect: "none" }}
              >
                Saved successfully
              </Typography>
            )}
        </DialogContent>
      </Dialog>
    );
  };

  const processRevert = React.useCallback(
    (newRow, oldRow) =>
      new Promise((resolve, reject) => {
        const mutation = computeMutation(newRow, oldRow);
        if (mutation) {
          setPromiseArguments({ resolve, reject, newRow, oldRow });
        } else {
          resolve(oldRow);
        }
      }),
    []
  );

  const handleSaveClick = (id) => () => {
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.View },
    }));
  };
  const noButtonRef = React.useRef(null);
  const [promiseArguments, setPromiseArguments] = React.useState(null);

  const handleRevertItem = async (id) => {
    const updatedRows = [...data[contextName]];
    const updatedRow = updatedRows?.find(
      (row) => parseInt(row?.[`${table}_uid`]) === parseInt(id)
    );
    updatedRow[`${table}_isDeleted`] = 0;

    let postStatus = 0;

    const token = await getAccessTokenSilently();
    const newRowData = {
      updates: [updatedRow],
    };
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };

    axios
      .post(`${API_ENDPOINTS[table]}`, newRowData, config)
      .then(function (response) {
        postStatus = response?.status;
        const newRows = [...data[contextName]];
        const newRow = response.data[table][0];
        newRow.uid = response.data[table][0][`${table}_uid`];
        const index = [...data[contextName]].findIndex(
          (i) => parseInt(i[`${table}_uid`]) === parseInt(id)
        );
        newRows[index] = newRow;
        setData({ ...data, [contextName]: newRows });
        if (postStatus === 200) {
          dispatch(
            toggleSnackbarOpen({
              children: "successfully reinstated",
              severity: "success",
              open: true,
            })
          );
        }
      })
      .catch(function (error) {
        postStatus = error?.response?.status;

        dispatch(
          toggleSnackbarOpen({
            children: `ERROR couldn't save data back to server. ${error?.response?.statusText}.`,
            severity: "error",
            open: true,
          })
        );
      });
  };

  const handleEditClick = React.useCallback(
    (params, event) => {
      const permitted = checkPermissions(userPermission, {
        page: gridList.gridPage,
        action: "edit",
        strict:
          gridList.gridPage === "modifiers" ||
          gridList.gridPage === "analysisCategories" ||
          gridList.gridPage === "macros" ||
          gridList.gridPage === "pricebands" ||
          gridList.gridPage === "paymentMethods" ||
          gridList.gridPage === "users" ||
          gridList.gridPage === "userTypes" ||
          gridList.gridPage === "policies"
            ? true
            : false,
      });
      if (
        checkPermissions(userPermission, {
          page: "permissions",
          strict: true,
          action: "edit",
        }) === true ||
        (permitted === true &&
          params?.row?.row?.user_number !== userPermission?.user_ref &&
          params?.row?.row?.userType_number !== userPermission?.user_type)
      ) {
        if (
          event &&
          params &&
          params.row !== undefined &&
          params?.row?.row?.tag_details?.isEditable !== false
        ) {
          if (
            typeof gridList.modalEdit === "undefined" ||
            gridList.modalEdit === false
          ) {
            setRowModesModel({
              [params.id]: { mode: GridRowModes.Edit },
            });
          } else {
            setModalConfig({
              focusField: "",
              editRow: params.row,
              timestamp: new Date(),
              isNew: false,
            });
            setModalVisible(true);
          }
        }
      }
    },
    [
      setRowModesModel,
      gridList,
      setModalConfig,
      setModalVisible,
      userPermission,
    ]
  );

  const handleDelete = async (id) => {
    let deletedRows;
    let variants = [];
    let selectedRows = [];
    if (gridList.gridPage === "screens") {
      selectedRows = rows?.filter((row) =>
        selectedRowsData?.includes(parseInt(row?.uid))
      );
      selectedRows?.forEach((screen) => {
        variants.push(
          data.screens?.filter(
            (i) =>
              parseInt(i?.list_number) === parseInt(screen?.list_number) &&
              i?.list_isDeleted === 0 &&
              i?.list_variant !== 0
          )
        );
      });

      deletedRows = [...selectedRows, ...variants].flat();
    } else {
      deletedRows = [...data[contextName]]?.filter((row) =>
        id.includes(parseInt(row?.uid))
      );
    }
    setOpen({ status: false, id: "", msg: null, lists: [] });
    const table = PAGE_TABLE_MAPPING[gridList.gridPage];
    if (
      variant !== undefined &&
      deletedRows.find((row) => row.uid === variant?.uid)
    ) {
      setVariant((draft) => {
        draft.list_isDeleted = 1;
      });
      deletedRows
        .filter((row) => row.uid !== variant?.uid)
        .forEach((row) => {
          row[table + "_isDeleted"] = 1;
        });
    } else {
      deletedRows.forEach((row) => {
        row[table + "_isDeleted"] = 1;
      });
    }
    let postStatus = 0;

    const token = await getAccessTokenSilently();
    const newRowData = {
      updates: deletedRows,
    };
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    axios
      .post(`${API_ENDPOINTS[table]}`, newRowData, config)
      .then(function (response) {
        postStatus = response?.status;
        if (postStatus === 200) {
          dispatch(
            toggleSnackbarOpen({
              message: `Successfully deleted the selected row${
                id.length === 1 ? "" : "s"
              }`,
              severity: "success",
              duration: 4000,
              open: true,
            })
          );
          setSelectedRowsData([]);
          setIsSaving(false);
        }

        setVariant();
        setTimeout(() => {
          if (showDeleted === false) {
            setRows(getRows(gridList, data, contextName, table, type));
          } else {
            setRows(getAllRows(gridList, data, contextName, table, type));
          }
        }, [1000]);
      })
      .catch(function (error) {
        dispatch(
          toggleSnackbarOpen({
            message: `Couldn't delete requested row(s), please try again`,
            severity: "error",
            duration: 8000,
            open: true,
          })
        );
        setVariant();
        deletedRows.forEach((row) => {
          row[table + "_isDeleted"] = 0;
        });
      });
  };

  const handleDeleteClick = (id) => () => {
    handleDelete(id);
  };

  const processRowUpdateBulk = React.useCallback((newRow, oldRow) => {
    setHasUnsavedRows(true);

    const rowId = newRow.uid;

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    return newRow;
  }, []);

  const saveChanges = () => {
    startTransition(() => {
      setIsSaving(true);
    });
  };
  React.useEffect(() => {
    setTimeout(() => {
      if (
        isSaving &&
        Object.values(unsavedChangesRef.current.unsavedRows).length > 0
      ) {
        saveBulkChanges();
      }
    }, [1000]);
  }, [isSaving]);

  const saveBulkChanges = async () => {
    const token = await getAccessTokenSilently();
    let num = 1;
    const rows = Object.values(unsavedChangesRef.current.unsavedRows);
    setSaveProgress({ openDialog: true, progress: 0 });
    processRowUpdateUtil(
      rows,
      false,
      token,
      setSaveProgress,
      table,
      unsavedChangesRef,
      setData,
      setSelectedRowsData,
      data,
      contextName,
      num,
      true
    ).then((res) => {
      setTimeout(() => {
        setHasUnsavedRows(false);
        setIsSaving(false);
        setSaveProgress({ openDialog: false, progress: 0 });
        blocker.proceed?.();
      }, [500]);
    });
  };

  const [discardingChanges, setIsDiscardingChanges] = React.useState(false);

  const discardChanges = () => {
    startTransition(() => {
      setIsDiscardingChanges(true);
    });
  };

  React.useEffect(() => {
    setTimeout(() => {
      if (discardingChanges) {
        onDiscard(unsavedChangesRef, ref)
          .then((res) => console.log(res))
          .finally(() => {
            unsavedChangesRef.current = {
              unsavedRows: {},
              rowsBeforeChange: {},
            };
            setIsDiscardingChanges(false);
            setHasUnsavedRows(false);
            blocker.proceed?.();
          });
      }
    }, [1000]);
  }, [discardingChanges]);

  const getRowClassName = React.useCallback((params) => {
    const unsavedRow = unsavedChangesRef.current.unsavedRows[params.id];
    if (unsavedRow) {
      if (unsavedRow._action === "delete") {
        return "row--removed";
      }
      return "row--edited";
    }

    if (
      params?.row[`${table}_isDeleted`] &&
      (params?.row[`${table}_isDeleted`] === 1 ||
        params?.row[`${table}_isDeleted`] === "1")
    ) {
      return "deletedRow";
    }
    if (params?.indexRelativeToCurrentPage === 0) {
      return "firstRow";
    }
    return "";
  }, []);

  const [selectedRowsData, setSelectedRowsData] = React.useState([]);

  React.useEffect(() => {
    if (
      Math.round(saveProgress.progress) >= 100 &&
      Object.keys(unsavedChangesRef.current.unsavedRows).length === 0
    ) {
      setSaveProgress({ openDialog: false, progress: 0 });
    }
  }, [saveProgress]);

  const processRowUpdate = async (newRow, originalRow, isNew) => {
    const token = await getAccessTokenSilently();
    processRowUpdateUtil(
      Array.isArray(newRow) ? newRow : [newRow],
      isNew ? isNew : false,
      token,
      setSaveProgress,
      table,
      unsavedChangesRef,
      setData,
      setSelectedRowsData,
      data,
      contextName
    )
      .then((res) => {
        setTimeout(() => {
          dispatch(
            toggleSnackbarOpen({
              message: "successfully saved",
              severity: "success",
              open: true,
            })
          );
          setModalVisible(false);
        });
        setIsSaving(false);
      })
      .catch((err) => {
        console.log(err);
        setTimeout(() => {
          dispatch(
            toggleSnackbarOpen({
              message: "couldnt save data",
              severity: "error",
              open: true,
            })
          );
        }).finally(() => {
          setIsSaving(false);
        });
      });
  };

  const handleNavigate = (item) => () => {
    if (gridList.gridPage === "screens") {
      setVariant();
      navigate("/designer", {
        state: { item: item.row },
      });
    } else if (gridList.gridPage === "items") {
      navigate(`/items/${item.row.plu_number}`, {
        state: { itemRow: item.row },
      });
    } else if (gridList.gridPage === "sites") {
      let id = item.id;

      navigate(`/sites/${id}`, {
        state: { item: item.row },
      });
    }
  };

  //
  const dataColumnsBuilder = [];
  for (let i = 0; i < gridList?.columns?.length; i++) {
    if (gridList.columns[i].visible === true) {
      if (gridList.columns[i].type === "priceModifier") {
        const newColumns = modiferDataColumns(
          gridList.columns[i],
          rows,
          data,
          classes,
          ref,
          priceBandColors,
          siteFilterValue,
          priceBandFilterValue,
          priceBandFilterValue
        );
        if (newColumns.length > 0) {
          dataColumnsBuilder.push(...newColumns);
        }
      } else {
        dataColumnsBuilder.push(gridList.columns[i]);
      }
    }
  }

  dataColumnsBuilder.push({
    field: "Actions",
    hideable: false,
    type: "actions",
    headerName: "Actions",
    flex: gridList.gridPage !== "pricing" && 1,
    cellClassName: "actions",
    allowNavigate: gridList.allowNavigate,
    getActions: (row) => {
      const isRowInEditMode = rowModesModel[row.id]?.mode === GridRowModes.Edit;
      const props = [
        userPermission,
        handleEditClick,
        data,
        setOpen,
        handleNavigate,
        setRowModesModel,
        handleSaveClick,
        processRevert,
        setHistoryModalOpen,
        rows,
        setRows,
      ];
      const key = Object.keys(rowModesModel)[0];
      const isInEditMode = rowModesModel[key]?.mode === GridRowModes.Edit;
      if (row?.row[`${table}_isDeleted`] === 1) {
        return [actionOptions(row, gridList, "History", ...props)];
      }
      if (
        (row?.row?.user_number === userPermission?.user_ref ||
          row?.row?.userType_number === userPermission?.user_type) &&
        checkPermissions(userPermission, {
          page: "permissions",
          strict: true,
          action: "edit",
        }) === false
      ) {
        return [actionOptions(row, gridList, "NoEdit", ...props)];
      }
      if (isRowInEditMode) {
        return [
          actionOptions(row, gridList, "Save", ...props),
          actionOptions(row, gridList, "Cancel", ...props),
        ];
      }
      if (gridList.allowNavigate !== true) {
        if (
          typeof gridList.rowEditingField === "undefined" ||
          getNestedValue(row.row, gridList.rowEditingField)
        ) {
          if (isInEditMode) {
            return [actionOptions(row, gridList, "Hidden", ...props)];
          }
          return [
            actionOptions(row, gridList, "Edit", ...props),
            actionOptions(row, gridList, "Delete", ...props),
            actionOptions(row, gridList, "History", ...props),
          ];
        } else {
          return [actionOptions(row, gridList, "NoEdit", ...props)];
        }
      } else if (isInEditMode) {
        return [actionOptions(row, gridList, "Hidden", ...props)];
      }
      if (
        gridList.gridPage === "screens" &&
        (row?.row?.list_details?.ignoreThisList === true ||
          row?.row?.list_details?.buttons?.some(
            (x) => x.functionType === 13
          )) &&
        checkPermissions(userPermission, {
          page: "screens",
          action: "disableTillSending",
          api: "GSAdmin",
          strict: true,
        }) === false
      ) {
        return [actionOptions(row, gridList, "Hidden", ...props)];
      } else
        return [
          actionOptions(row, gridList, "Edit", ...props),
          actionOptions(row, gridList, "Delete", ...props),
          actionOptions(row, gridList, "History", ...props),
          actionOptions(row, gridList, "More", ...props),
        ];
    },
  });

  const handleMultiDeleteConfirmation = () => {
    handleDelete(selectedRowsData);
    setOpenDialog(false);
  };

  const handleCellKeyDown = React.useCallback((params, event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [params.id]: { mode: GridRowModes.View },
      }));
    }
  }, []);

  function Pagination({ page, onPageChange, className }) {
    const pageCount = useGridSelector(ref, gridPageCountSelector);

    return (
      <MuiPagination
        color="primary"
        className={className}
        count={pageCount}
        page={page + 1}
        onChange={(event, newPage) => {
          onPageChange(event, newPage - 1);
        }}
        size="small"
        shape="rounded"
      />
    );
  }

  const autosizeOptions = {
    includeOutliers: true,
  };

  React.useEffect(() => {
    if (blocker.state === "blocked" && !hasUnsavedRows) {
      blocker.reset();
    }
    if (blocker.state === "blocked" && hasUnsavedRows) {
      setErrorMessage({
        open: true,
        text: `You have unfinished changes. Please save or discard any changes before continuing.`,
        button1text: "Discard Changes",
        button1func: discardChanges,
        button2text: "Save Changes",
        button2func: saveChanges,
      });
    }
  }, [blocker, hasUnsavedRows]);

  const checkboxSelectionVisibleOnly = (newRowSelectionModel, details) => {
    const {
      state: {
        pagination: {
          paginationModel: { page, pageSize },
        },
      },
    } = ref.current;
    if (selectedRowsData.length === 0) {
      if (newRowSelectionModel.length > 1) {
        const startIndex = page * pageSize;
        const endIndex = startIndex + pageSize;
        const filteredRows = gridFilteredSortedRowEntriesSelector(ref)
          .slice(startIndex, endIndex)
          .map((x) => x.id);
        setSelectedRowsData([...selectedRowsData, ...filteredRows]);
      } else {
        setSelectedRowsData([...selectedRowsData, ...newRowSelectionModel]);
      }
    } else {
      setSelectedRowsData(newRowSelectionModel);

      if (newRowSelectionModel.length < selectedRowsData.length) {
        setSelectedRowsData(newRowSelectionModel);
      }
    }
  };

  return (
    <>
      {renderConfirmDialog(
        noButtonRef,
        promiseArguments,
        setPromiseArguments,
        handleRevertItem
      )}

      <MultiDeleteDialog
        gridList={gridList}
        rows={rows}
        selectedRowsData={selectedRowsData}
        data={data}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        handleMultiDeleteConfirmation={handleMultiDeleteConfirmation}
      />
      {renderProgressDialog()}
      <ErrorDialog
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <ConfirmBox
        deleteFunction={handleDeleteClick(open?.id)}
        open={open}
        gridList={gridList}
        page={gridList.gridPage}
        closeDialog={() =>
          setOpen({
            status: false,
            id: null,
            msg: null,
            lists: [],
          })
        }
      />
      <GridModal
        modalConfig={modalConfig}
        gridList={gridList}
        setModalConfig={setModalConfig}
        modalVisible={modalVisible}
        defaultValues={modalConfig?.editRow?.row}
        setModalVisible={setModalVisible}
        processRowUpdate={processRowUpdate}
        contextName={contextName}
        table={table}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
      />
      <BulkPricingModal
        ref={ref}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        unsavedChangesRef={unsavedChangesRef}
        processRowUpdateBulk={processRowUpdateBulk}
        selectedRowsData={selectedRowsData}
        setSelectedRowsData={setSelectedRowsData}
        priceBandFilterValue={priceBandFilterValue}
        siteFilterValue={siteFilterValue}
      />
      <HistoryModal
        modalConfig={historyModalOpen}
        gridList={gridList}
        setModalConfig={setHistoryModalOpen}
        processRowUpdate={processRowUpdate}
        contextName={contextName}
        table={table}
        apiRef={historyApiRef}
      />
      <DataGrid
        rows={rows}
        columns={dataColumnsBuilder}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
          columns: {
            columnVisibilityModel,
          },
        }}
        autosizeOptions={autosizeOptions}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => {
          setFilterModel(newFilterModel);
        }}
        autoPageSize={false}
        getRowHeight={() => "auto"}
        getRowId={(row) => parseInt(row[`uid`])}
        sx={{
          "& .firstRow": {
            "& .tableControl": {
              display: state.stepIndex === 4 ? "unset" : "none",
            },
          },
          [`& .${gridClasses.row}.row--removed`]: {
            backgroundColor: (theme) => {
              if (theme.palette.mode === "light") {
                return "rgba(166, 222, 187,1)";
              }
              return darken("rgba(166, 222, 187,1)", 0.7);
            },
          },
          [`& .${gridClasses.row}.row--edited`]: {
            backgroundColor: (theme) => {
              if (theme.palette.mode === "light") {
                return "rgba(166, 222, 187,1)";
              }
              return darken("rgba(166, 222, 187,1)", 0.6);
            },
          },
        }}
        apiRef={ref}
        showCellVerticalBorder
        showCellHorizontalBorder
        editMode="row"
        columnHeaderHeight={gridList.gridPage === "pricing" ? 90 : 56}
        rowModesModel={rowModesModel}
        onRowEditStop={handleRowEditStop}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        onCellKeyDown={handleCellKeyDown}
        density={"compact"}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
          noResultsOverlay: CustomOverlay,
          footer: Footer,
          columnMenu: CustomColumnMenu,
        }}
        getRowClassName={getRowClassName}
        getCellClassName={(params) => {
          if (
            params.field === "printer_details.sites" ||
            params.field === "printer_details.site_name" ||
            params.field === "printer_details.site_ref"
          ) {
            return "printerDetails";
          }
        }}
        slotProps={{
          toolbar: {
            data,
            modalVisible,
            setModalVisible,
            modalConfig,
            setModalConfig,
            setData,
            setRowModesModel,
            gridList,
            selectedRowsData,
            // setSnackbar,
            searchValue,
            handleSearchValueChange,
            handleClearSearchValueChange,
            unsavedChangesRef: unsavedChangesRef,
            hasUnsavedRows,
            table,
            saveChanges,
            discardChanges,
            discardingChanges,
            isSaving,
            contextName,
            showDeleted,
            filterModel,
            setFilterModel,
            setShowDeleted,
          },
          footer: {
            Pagination,
            gridList,
            selectedRowsData,
            userPermission,
            setOpenDialog,
          },
          columnsPanel: {
            getTogglableColumns,
          },
          getActions: gridList,

          columnHeader: {
            className: classes.headerWrap,
          },
          loadingOverlay: {
            variant: "skeleton",
          },
        }}
        checkboxSelection
        checkboxSelectionVisibleOnly={true}
        disableRowSelectionOnClick
        onRowDoubleClick={(params, event) => {
          event.stopPropagation();

          handleEditClick({ id: params.id, row: params }, event);
        }}
        isRowSelectable={(params) => {
          if (
            checkPermissions(userPermission, {
              page: "permissions",
              strict: true,
              action: "edit",
            })
          ) {
            return true;
          } else if (
            params?.row?.user_number === userPermission?.user_ref ||
            params?.row?.userType_number === userPermission?.user_type
          ) {
            return false;
          } else {
            return true;
          }
        }}
        rowSelectionModel={selectedRowsData}
        onRowSelectionModelChange={(newRowSelectionModel, details) => {
          checkboxSelectionVisibleOnly(newRowSelectionModel, details);
        }}
        processRowUpdate={(newRow, oldRow) => {
          if (newRow !== oldRow) {
            if (
              gridList.gridPage === "pricing" &&
              checkPermissions(userPermission, {
                page: "pricing",
                action: "bulkPriceEdit",
                strict: true,
              }) === true
            ) {
              processRowUpdateBulk(newRow, oldRow);
            } else processRowUpdate(newRow, oldRow);
          }
          return newRow;
        }}
        experimentalFeatures={{ newEditingApi: true }}
        loading={rowLoading}
      />
    </>
  );
}
